import {BackendResponse} from './backend-response/backend-response';
import {apiClient} from './query-client';
import {
  BackendFilter,
  FilterControlType,
  FilterOperator,
} from '@common/datatable/filters/backend-filter';
import {message} from '@ui/i18n/message';
import {
  createdAtFilter,
  updatedAtFilter,
} from '@common/datatable/filters/timestamp-filters';
import {useQuery} from '@tanstack/react-query';
import {
  UseFiltersReturn,
  useLinkFiltersData,
} from '@app/liens/use-link-filters';
import {Title} from '@app/titles/models/title';

export interface FetchLinksDataResponse extends BackendResponse {
  filters: {
    qualites: number[];
    hosts: number[];
    langues: number[];
    subs: number[];
  };
}

interface Options {
  disabled?: boolean;
}

export function useLinksFilters(title_id: number) {
  return useQuery({
    queryKey: ['links-filters', `${title_id}`],
    queryFn: () => fetchLinksData(title_id!),
  });
}

function fetchLinksData(title_id: number): Promise<FetchLinksDataResponse> {
  return apiClient
    .get(`titles/${title_id}/links-filters`)
    .then(response => response.data);
}

export const LinksIndexPageFilters = ({
  data,
  filters,
  title,
}: {
  data: FetchLinksDataResponse;
  filters?: UseFiltersReturn;
  title: Title;
}): BackendFilter[] => {
  const hostsArray = Object.values(filters?.hosts || {});
  const qualitiesArray = Object.values(filters?.qualities || {});
  const languagesArray = Object.values(filters?.languages || {});
  const subsArray = Object.values(filters?.subs || {});

  const filtersArray: BackendFilter[] = [
    {
      key: `id_host`,
      label: message('Hosts'),
      defaultOperator: FilterOperator.eq,
      control: {
        type: FilterControlType.Select,
        options: data.filters.hosts.map(hostId => {
          const host = hostsArray.find(h => h.id === hostId);
          return {
            key: hostId,
            label: host ? host.name : `Host ${hostId}`,
            value: hostId,
          };
        }),
      },
    },
    {
      key: `qualite`,
      label: message('Qualities'),
      defaultOperator: FilterOperator.eq,
      control: {
        type: FilterControlType.Select,
        options: data.filters.qualites.map(qualiteId => {
          const qualite = qualitiesArray.find(q => q.id === qualiteId);
          return {
            key: qualiteId,
            label: qualite ? qualite.name : `Quality ${qualiteId}`,
            value: qualiteId,
          };
        }),
      },
    },
    {
      key: `langues`,
      label: message('Languages'),
      defaultOperator: FilterOperator.has,
      control: {
        type: FilterControlType.Select,
        options: data.filters.langues.map(langueId => {
          const langue = languagesArray.find(l => l.id === langueId);
          return {
            key: langueId,
            label: langue ? langue.name : `Language ${langueId}`,
            value: langueId,
          };
        }),
      },
    },
    {
      key: `subs`,
      label: message('Subtitles'),
      defaultOperator: FilterOperator.has,
      control: {
        type: FilterControlType.Select,
        options: data.filters.subs.map(subId => {
          const sub = subsArray.find(s => s.id === subId);
          return {
            key: subId,
            label: sub ? sub.name : `Subtitle ${subId}`,
            value: subId,
          };
        }),
      },
    },
    createdAtFilter({
      description: message('Date link was created'),
    }),
    updatedAtFilter({
      description: message('Date link was last updated'),
    }),
  ];

  if (title.is_series) {
    filtersArray.push({
      key: `episode`,
      label: message('Episode'),
      defaultOperator: FilterOperator.eq,
      control: {
        type: FilterControlType.Input,
        inputType: 'number',
        minValue: 1,
        defaultValue: 1,
      },
    });
  }

  return filtersArray;
};
